/**
 * useWorldCupLeaderboardData — Adapter hook for the Leaderboard (global leaderboard) tab.
 *
 * Fetches the global competition leaderboard from:
 *   GET /competitions/:competitionId/leaderboard?page=1&limit=50
 */

import { useMemo } from 'react';
import { useSupabaseAuth } from '../../shared/contexts/SupabaseAuthContext';
import { useCompetitionId } from '../../competitions/contexts/CompetitionContext';
import { mapLeaderboardApiEntry } from '../helpers/worldcup.helper';
import { resolveLeaderboardSelfRow } from '../helpers/leaderboardSelfRow.helper';
import { useWorldCupLeaderboardQuery } from './useWorldCupLeaderboardQuery';

import type { LeaderboardEntry } from '../types/worldcup.types';

interface WorldCupLeaderboardData {
  leaderboard: LeaderboardEntry[];
  top3: LeaderboardEntry[];
  rest: LeaderboardEntry[];
  /**
   * The caller's row regardless of where it sits — the exact list instance (so
   * the parent can `scrollToItem` it by reference). Null when the caller has no
   * row. Powers the always-visible "your standing" bar.
   */
  currentUserEntry: LeaderboardEntry | null;
  /** True when the caller's row is within the loaded top-N slice. */
  isCurrentUserOnPage: boolean;
  totalParticipants: number;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
}

export function useWorldCupLeaderboardData(): WorldCupLeaderboardData {
  const { isSignedIn, isLoaded } = useSupabaseAuth();
  // Competition comes from context: WC provider → 'world-cup-2026' (unchanged);
  // competition shell → its route param.
  const competitionId = useCompetitionId();
  const canFetch = isLoaded && isSignedIn;
  const leaderboardQuery = useWorldCupLeaderboardQuery(competitionId, {
    enabled: canFetch,
    limit: 50,
  });
  const leaderboard = useMemo(() => {
    if (!isLoaded || !isSignedIn) return [];
    const data = leaderboardQuery.data;
    if (!data || data.entries.length === 0) return [];

    /**
     * Entries arrive already sorted (points desc, hits desc) but the API only sets
     * `rank` on the Redis fast path — the DB fallback omits it. Derive rank from
     * sorted-array index so the podium can always place ranks 1/2/3 correctly.
     */
    const mapped = data.entries.map((entry, index) => ({
      ...mapLeaderboardApiEntry(entry),
      rank: entry.rank ?? index + 1,
    }));
    // The off-page caller is surfaced via the floating "your standing" bar only —
    // appending them here would also render them as an in-list card (duplicate).
    return mapped;
  }, [leaderboardQuery.data, isLoaded, isSignedIn]);

  const totalParticipants = useMemo(() => {
    if (!isLoaded || !isSignedIn) return 0;
    return leaderboardQuery.data?.totalParticipants ?? leaderboard.length;
  }, [leaderboardQuery.data?.totalParticipants, isLoaded, isSignedIn, leaderboard.length]);

  const isLoading = !isLoaded || (isSignedIn && leaderboardQuery.isLoading);
  const isFetching = isSignedIn && leaderboardQuery.isFetching;
  const isError = Boolean(isSignedIn && leaderboardQuery.isError);

  const { top3, rest, currentUserEntry, isCurrentUserOnPage } = useMemo(() => {
    const rawEntries = leaderboardQuery.data?.entries ?? [];
    const apiCurrentUser = leaderboardQuery.data?.currentUser;

    /**
     * Slice by sorted-array position, not by `rank`, so ties at rank 1 don't
     * dump everyone into top3 and leave `rest` empty.
     */
    const top3Slice = leaderboard.slice(0, 3);
    const restSlice = leaderboard.slice(3);
    // On-page: the caller's natural list row (so the bar can scroll to it by
    // reference). Off-page: reconstruct from the server `currentUser` payload —
    // the floating "your standing" bar is the caller's only self-indicator, so
    // we no longer pin them in the list footer (that duplicated the bar).
    const onPageEntry = leaderboard.find((e) => e.isCurrentUser) ?? null;
    const offPageEntry = apiCurrentUser ? mapLeaderboardApiEntry(apiCurrentUser) : null;
    const self = resolveLeaderboardSelfRow({ rawEntries, onPageEntry, offPageEntry });

    return { top3: top3Slice, rest: restSlice, ...self };
  }, [leaderboard, leaderboardQuery.data?.entries, leaderboardQuery.data?.currentUser]);

  return {
    leaderboard,
    top3,
    rest,
    currentUserEntry,
    isCurrentUserOnPage,
    totalParticipants,
    isLoading,
    isFetching,
    isError,
  };
}
